import { useEffect, useMemo, useRef, useState } from "react";
import { fieldPlaceholder, groupFieldRows, initialApiKeyValues, isSecretField } from "../../../lib/apiKeyFields";
import type { ApiKeyField } from "../../../types/integration";
import styles from "./ApiKeyConnectModal.module.css";

export type ApiKeySubmitResult = { ok: true } | { ok: false; message: string };

type Props = {
  open: boolean;
  // Integration title (e.g. "Algolia") for the dialog copy.
  title: string;
  // Required credential fields, from the integration's stored schema. One input
  // is rendered per field. Empty -> a single default "API key" field (for a
  // provider that carries no schema).
  fields: ReadonlyArray<ApiKeyField>;
  // Where the user finds their key, rendered as helper text inside the modal.
  helpText?: string | undefined;
  helpHref?: string | undefined;
  helpLinkLabel?: string | undefined;
  // IP-422: optional block rendered between the header and the fields — the
  // BYO OAuth flow uses it for setup instructions + the copyable redirect URL.
  preamble?: JSX.Element | undefined;
  onClose: () => void;
  // One value per field, keyed by field `name`.
  onSubmit: (credentials: Record<string, string>) => Promise<ApiKeySubmitResult>;
};

const DEFAULT_FIELD: ApiKeyField = {
  name: "api_key",
  displayName: "API key",
  type: "string",
  description: null,
};

const effectiveFieldsOf = (fields: ReadonlyArray<ApiKeyField>): ReadonlyArray<ApiKeyField> =>
  fields.length > 0 ? fields : [DEFAULT_FIELD];

// Generic API-key connect modal. Renders a form from the integration's
// credential schema — one input per required field — so multi-field toolkits
// (key + subdomain, ...) and single-key providers share the same flow. Uses the
// native <dialog> + showModal() like DeleteModal for focus-trap / Escape / backdrop.
export const ApiKeyConnectModal = ({
  open,
  title,
  fields,
  helpText,
  helpHref,
  helpLinkLabel,
  preamble,
  onClose,
  onSubmit,
}: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const effectiveFields = useMemo(() => effectiveFieldsOf(fields), [fields]);
  const [values, setValues] = useState<Record<string, string>>(() => initialApiKeyValues(effectiveFields));
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Read on open only: re-seeding defaults when a parent re-renders would wipe
  // what the user has typed.
  const fieldsRef = useRef(effectiveFields);
  fieldsRef.current = effectiveFields;

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setValues(initialApiKeyValues(fieldsRef.current));

      setRevealed({});
      setServerError(null);
      setSubmitting(false);
      window.setTimeout(() => firstInputRef.current?.focus(), 0);
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  if (!open) return null;

  const allFilled = effectiveFields.every((f) => (values[f.name] ?? "").trim().length > 0);
  const disabled = submitting || !allFilled;

  const handleSubmit = async (evt: React.FormEvent): Promise<void> => {
    evt.preventDefault();
    if (disabled) return;
    setSubmitting(true);
    setServerError(null);
    const credentials: Record<string, string> = {};
    for (const f of effectiveFields) credentials[f.name] = (values[f.name] ?? "").trim();
    const result = await onSubmit(credentials);
    if (result.ok) {
      onClose();
      return;
    }
    setSubmitting(false);
    setServerError(result.message);
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting) onClose();
      }}
    >
      <form className={styles.form} onSubmit={handleSubmit}>
        <header className={styles.header}>
          <h2 className={styles.title}>Connect {title}</h2>
          {helpText ? (
            <p className={styles.subtitle}>
              {helpText}
              {helpHref ? (
                <>
                  {" "}
                  <a className={styles.helpLink} href={helpHref} target="_blank" rel="noreferrer noopener">
                    {helpLinkLabel ?? "Open"}
                    <span aria-hidden> →</span>
                  </a>
                </>
              ) : null}
            </p>
          ) : null}
        </header>

        {preamble ?? null}

        {groupFieldRows(effectiveFields).map((row, rowIdx) => {
          const renderField = (field: ApiKeyField, isFirst: boolean): JSX.Element => {
            const secret = isSecretField(field);
            const show = revealed[field.name] ?? false;
            return (
              <label className={styles.field} key={field.name}>
                <span className={styles.label}>{field.displayName}</span>
                <div className={styles.inputWrap}>
                  <input
                    ref={isFirst ? firstInputRef : undefined}
                    type={secret && !show ? "password" : "text"}
                    className={styles.input}
                    value={values[field.name] ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setValues((prev) => ({ ...prev, [field.name]: v }));
                      setServerError(null);
                    }}
                    placeholder={fieldPlaceholder(field)}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={submitting}
                  />
                  {secret ? (
                    <button
                      type="button"
                      className={styles.revealBtn}
                      onClick={() => setRevealed((prev) => ({ ...prev, [field.name]: !show }))}
                      aria-label={show ? `Hide ${field.displayName}` : `Show ${field.displayName}`}
                      aria-pressed={show}
                      disabled={submitting}
                      tabIndex={-1}
                    >
                      {show ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  ) : null}
                </div>
                {field.description ? <span className={styles.hint}>{field.description}</span> : null}
              </label>
            );
          };
          if (row.length === 1 && row[0]) return renderField(row[0], rowIdx === 0);
          return (
            <div
              className={styles.fieldRow}
              key={row.map((f) => f.name).join("+")}
              style={{ "--row-extra": row.length - 1 } as React.CSSProperties}
            >
              {row.map((field, i) => renderField(field, rowIdx === 0 && i === 0))}
            </div>
          );
        })}

        {serverError && <div className={styles.errorBanner}>{serverError}</div>}

        <footer className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={disabled}>
            {submitting ? "Connecting…" : "Connect"}
          </button>
        </footer>
      </form>
    </dialog>
  );
};

const EyeIcon = (): JSX.Element => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (): JSX.Element => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);
